// src/frontend/js/modern/lib/dropdown-lite.js

/**
 * DropdownLite - Modern Vanilla JS implementation replacing WlfmcDropDown
 * Supports animations, focus management, filtering, and custom events.
 */
export class DropdownLite {
	constructor(input, options = {}) {
		this.input = input;
		this.options = {
			filter: true,
			effects: 'default', // default, fade, slide
			duration: 'fast',   // fast, slow, or number in ms
			trigger: 'focus',   // focus, manual
			...options
		};

		this._visible = false;
		this._isAnimating = false;
		this._dropdownId = input.dataset.dropdown;
		this._list = this._dropdownId ? document.getElementById(this._dropdownId) : null;

		// Normalize duration to milliseconds
		this._durationMs = this._getDurationMs(this.options.duration);

		if (!this._list) return;

		this._init();
	}

	/**
	 * Initialize the dropdown instance
	 */
	_init() {
		this.input.classList.add('wlfmc-dropdown-input');

		// Bind methods to preserve context
		this._handlers = {
			input: () => this._filter(),
			keydown: (e) => this._onKeyDown(e),
			focus: () => this._handleFocus(),
			blur: () => this._handleBlur(),
			listMouseMove: (e) => this._onListMouseMove(e),
			listMouseDown: (e) => this._onListMouseDown(e),
			listClick: (e) => this._onListClick(e),
			listMouseUp: () => this._onListMouseUp()
		};

		// Input events
		this.input.addEventListener('input', this._handlers.input);
		this.input.addEventListener('keydown', this._handlers.keydown);

		if (this.options.trigger === 'focus') {
			this.input.addEventListener('focus', this._handlers.focus);
			this.input.addEventListener('blur', this._handlers.blur);
		}

		// List events
		this._list.addEventListener('mousemove', this._handlers.listMouseMove);
		this._list.addEventListener('mousedown', this._handlers.listMouseDown);
		this._list.addEventListener('click', this._handlers.listClick);
		this._list.addEventListener('mouseup', this._handlers.listMouseUp);

		// Initial filter state
		this._filter();

		// Trigger created event
		this._trigger('created');
	}

	/**
	 * Convert duration option to milliseconds
	 */
	_getDurationMs(duration) {
		if (typeof duration === 'number') return duration;
		switch (duration) {
			case 'slow': return 600;
			case 'fast':
			default: return 200;
		}
	}

	/**
	 * Handle input focus - show dropdown immediately
	 */
	_handleFocus() {
		this.input.classList.remove('wlfmc-pulse');
		this.show();
	}

	/**
	 * Handle input blur with intelligent delay
	 * Prevents closing when user clicks on list items or is still interacting
	 */
	_handleBlur() {
		// Small delay to allow click events on list to fire first
		setTimeout(() => {
			const activeEl = document.activeElement;
			const isHoveringList = this._list.matches(':hover');
			const isFocusedInList = this._list.contains(activeEl);
			const isInputFocused = activeEl === this.input;

			// Only hide if user has truly left the component
			if (!isHoveringList && !isFocusedInList && !isInputFocused) {
				this.hide();
			} else if (!isInputFocused && (isHoveringList || isFocusedInList)) {
				// Keep focus on input if user is interacting with list
				// This maintains the "always open while focused" behavior
				requestAnimationFrame(() => {
					if (this._visible) this.input.focus();
				});
			}
		}, 150);
	}

	/**
	 * Show dropdown with configured animation
	 */
	show() {
		if (!this._list || this._isAnimating) return;

		// Position calculation before showing
		const inputRect = this.input.getBoundingClientRect();
		Object.assign(this._list.style, {
			position: 'absolute',
			width: inputRect.width + 'px',
			zIndex: '99999',
			top: (this.input.offsetTop + this.input.offsetHeight - 1) + 'px',
			left: this.input.offsetLeft + 'px'
		});

		// Check for visible items after filtering
		const visibleItems = Array.from(this._list.querySelectorAll('li'))
			.filter(li => li.style.display !== 'none');

		if (visibleItems.length === 0) return;

		this._visible = true;
		this.input.classList.add('open');
		this._trigger('show');

		// Apply animation based on effect type
		this._animateShow().then(() => {
			this._trigger('shown');
		});
	}

	/**
	 * Hide dropdown with configured animation
	 */
	hide() {
		if (!this._visible || this._isAnimating || !this._list) return;

		this._trigger('hide');
		this.input.classList.remove('open');

		this._animateHide().then(() => {
			this._visible = false;
			this._trigger('hidden');
		});
	}

	/**
	 * Animation handler for showing dropdown
	 */
	_animateShow() {
		return new Promise(resolve => {
			this._isAnimating = true;
			const effect = this.options.effects;
			const duration = this._durationMs;

			if (effect === 'fade') {
				this._list.style.opacity = '0';
				this._list.style.display = 'block';

				const animation = this._list.animate([
					{ opacity: 0 },
					{ opacity: 1 }
				], { duration, easing: 'ease-out' });

				animation.onfinish = () => {
					this._list.style.opacity = '1';
					this._isAnimating = false;
					resolve();
				};
			} else if (effect === 'slide') {
				this._list.style.height = '0';
				this._list.style.overflow = 'hidden';
				this._list.style.display = 'block';

				// Calculate natural height
				const naturalHeight = this._list.scrollHeight;

				const animation = this._list.animate([
					{ height: '0px', opacity: 0 },
					{ height: naturalHeight + 'px', opacity: 1 }
				], { duration, easing: 'ease-out' });

				animation.onfinish = () => {
					this._list.style.height = '';
					this._list.style.overflow = '';
					this._list.style.opacity = '1';
					this._isAnimating = false;
					resolve();
				};
			} else {
				// Default instant show
				this._list.style.display = 'block';
				this._isAnimating = false;
				resolve();
			}
		});
	}

	/**
	 * Animation handler for hiding dropdown
	 */
	_animateHide() {
		return new Promise(resolve => {
			this._isAnimating = true;
			const effect = this.options.effects;
			const duration = this._durationMs;

			if (effect === 'fade') {
				const animation = this._list.animate([
					{ opacity: 1 },
					{ opacity: 0 }
				], { duration, easing: 'ease-in' });

				animation.onfinish = () => {
					this._list.style.display = 'none';
					this._list.style.opacity = '';
					this._isAnimating = false;
					resolve();
				};
			} else if (effect === 'slide') {
				const currentHeight = this._list.offsetHeight;
				this._list.style.overflow = 'hidden';

				const animation = this._list.animate([
					{ height: currentHeight + 'px', opacity: 1 },
					{ height: '0px', opacity: 0 }
				], { duration, easing: 'ease-in' });

				animation.onfinish = () => {
					this._list.style.display = 'none';
					this._list.style.height = '';
					this._list.style.overflow = '';
					this._list.style.opacity = '';
					this._isAnimating = false;
					resolve();
				};
			} else {
				// Default instant hide
				this._list.style.display = 'none';
				this._isAnimating = false;
				resolve();
			}
		});
	}

	/**
	 * Filter list items based on input value
	 */
	_filter() {
		if (!this._list) return;

		const search = this.input.value.toLowerCase().trim();
		let hiddenCount = 0;
		const items = this._list.querySelectorAll('li');

		items.forEach(li => {
			const text = li.textContent.toLowerCase();
			const match = !this.options.filter || text.indexOf(search) > -1;

			li.style.display = match ? '' : 'none';
			if (match) {
				li.classList.add('es-visible');
			} else {
				li.classList.remove('es-visible');
				hiddenCount++;
			}
		});

		// Auto-hide if no results
		if (items.length > 0 && hiddenCount === items.length) {
			this.hide();
		} else if (this._visible && this.options.filter) {
			// Re-position if needed after filter
			requestAnimationFrame(() => this._ensureVisibleItemHighlighted());
		}
	}

	/**
	 * Ensure at least one item is highlighted when dropdown opens/filters
	 */
	_ensureVisibleItemHighlighted() {
		const selected = this._list.querySelector('li.selected.es-visible');
		if (!selected) {
			const firstVisible = this._list.querySelector('li.es-visible:not([disabled])');
			if (firstVisible) this._highlight(firstVisible);
		}
	}

	/**
	 * Highlight a specific list item
	 */
	_highlight(li) {
		this._list.querySelectorAll('.selected').forEach(el => el.classList.remove('selected'));
		li.classList.add('selected');

		// Scroll into view if needed
		const rect = li.getBoundingClientRect();
		const listRect = this._list.getBoundingClientRect();

		if (rect.top < listRect.top) {
			this._list.scrollTop -= listRect.top - rect.top;
		} else if (rect.bottom > listRect.bottom) {
			this._list.scrollTop += rect.bottom - listRect.bottom;
		}
	}

	/**
	 * Select a list item
	 */
	_select(li) {
		if (!li || li.hasAttribute('disabled') || !li.classList.contains('es-visible')) return;

		this.input.value = li.textContent.trim();

		if (this.options.filter) {
			this.hide();
		}

		this._filter();
		this._trigger('select', li);
	}

	/**
	 * Keyboard navigation handler
	 */
	_onKeyDown(e) {
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				this._highlightNext(1);
				break;
			case 'ArrowUp':
				e.preventDefault();
				this._highlightNext(-1);
				break;
			case 'Enter':
				if (this._visible) {
					e.preventDefault();
					const selected = this._list.querySelector('li.selected');
					if (selected) this._select(selected);
				}
				break;
			case 'Escape':
			case 'Tab':
				this.hide();
				break;
			default:
				// For any other key, ensure dropdown is open and highlight reset
				if (this._visible) {
					requestAnimationFrame(() => this._ensureVisibleItemHighlighted());
				}
				break;
		}
	}

	/**
	 * Navigate through visible items
	 */
	_highlightNext(direction) {
		const visibles = Array.from(this._list.querySelectorAll('li.es-visible:not([disabled])'));
		if (visibles.length === 0) return;

		const current = this._list.querySelector('li.selected');
		let idx = current ? visibles.indexOf(current) : -1;

		// Circular navigation
		idx = (idx + direction + visibles.length) % visibles.length;

		this._highlight(visibles[idx]);
	}

	/**
	 * Mouse move handler for list hover highlighting
	 */
	_onListMouseMove(e) {
		const li = e.target.closest('li:not([disabled])');
		if (!li || !this._list.contains(li)) return;
		this._highlight(li);
	}

	/**
	 * Mouse down handler for selection
	 */
	_onListMouseDown(e) {
		const li = e.target.closest('li');
		if (!li || !this._list.contains(li)) return;

		if (li.hasAttribute('disabled')) {
			e.preventDefault();
			return;
		}

		// Prevent blur from firing before selection completes
		e.preventDefault();

		if (!li.classList.contains('list-item')) {
			this._select(li);
		}
	}

	/**
	 * Click handler for anchor links within list
	 */
	_onListClick(e) {
		const link = e.target.closest('a.select-list');
		if (!link || !this._list.contains(link)) return;

		e.preventDefault();
		const li = link.closest('li');
		if (li) this._select(li);
	}

	/**
	 * Clean up selected state on mouse up
	 */
	_onListMouseUp() {
		this._list.querySelectorAll('li.selected').forEach(el => el.classList.remove('selected'));
	}

	/**
	 * Dispatch custom event with namespace compatibility
	 */
	_trigger(event, detail = null) {
		const customEvent = new CustomEvent(`${event}.editable-select`, {
			bubbles: true,
			cancelable: true,
			detail
		});
		this.input.dispatchEvent(customEvent);
	}

	/**
	 * Cleanup and destroy instance
	 */
	destroy() {
		this.input.classList.remove('wlfmc-dropdown-input', 'open');

		// Remove all event listeners
		if (this._handlers) {
			this.input.removeEventListener('input', this._handlers.input);
			this.input.removeEventListener('keydown', this._handlers.keydown);
			this.input.removeEventListener('focus', this._handlers.focus);
			this.input.removeEventListener('blur', this._handlers.blur);

			if (this._list) {
				this._list.removeEventListener('mousemove', this._handlers.listMouseMove);
				this._list.removeEventListener('mousedown', this._handlers.listMouseDown);
				this._list.removeEventListener('click', this._handlers.listClick);
				this._list.removeEventListener('mouseup', this._handlers.listMouseUp);
			}
		}

		// Reset list styles
		if (this._list) {
			this._list.style.display = '';
			this._list.style.opacity = '';
			this._list.style.height = '';
			this._list.style.overflow = '';
		}

		// Remove instance data
		delete this.input._dropdownLiteInstance;
	}
}
